import type { ReplicantModule, ReplicantModulesState } from './ReplicantModules';
import { EmptyRuleset, Ruleset } from './ReplicantRuleset';
import type { SharedStates } from './ReplicantSharedStates';
import SB from './SchemaBuilder';
import { WithMeta } from './systemStateFields';
import type { Filter, Namespaced } from './utils/TypeUtils';

export type MessageInfo = {
    senderId: string;

    /** A timestamp of when the message was sent. */
    timestamp: number;
};

export type UserMessageSignature<
    TUserState = any,
    TSchema extends SB.Schema = SB.Schema,
    TRuleset extends Ruleset = EmptyRuleset,
    TSharedStates extends SharedStates = {},
> = {
    schema: TSchema;
    reducer: (
        state: WithMeta<TUserState, TRuleset, TSharedStates>,
        args: SB.ExtractType<TSchema>,
        messageInfo: MessageInfo,
    ) => void;
    isAdmin: false;
};

export type AdminMessageSignature<
    TUserState = any,
    TSchema extends SB.Schema = SB.Schema,
    TRuleset extends Ruleset = EmptyRuleset,
    TSharedStates extends SharedStates = {},
> = {
    schema: TSchema;
    reducer: (
        state: WithMeta<TUserState, TRuleset, TSharedStates>,
        args: SB.ExtractType<TSchema>,
        messageInfo: MessageInfo,
    ) => void;
    description: string;
    isAdmin: true;
};

export type MessageSignature<
    TUserState,
    TRuleset extends Ruleset = EmptyRuleset,
    TSharedStates extends SharedStates = {},
> =
    | UserMessageSignature<TUserState, any, TRuleset, TSharedStates>
    | AdminMessageSignature<TUserState, any, TRuleset, TSharedStates>;

export type Messages<
    TUserState = any,
    TRuleset extends Ruleset = EmptyRuleset,
    TSharedStates extends SharedStates = {},
    TModules extends ReplicantModule[] = [],
> = Namespaced<MessageSignature<TUserState & ReplicantModulesState<TModules>, TRuleset, TSharedStates>>;

/**
 * @param schema Message arguments schema. Use an empty object `SB.object({})` schema in case of no arguments; this makes it easier to extend the message with arguments later on.
 */
export function createMessage<
    T extends SB.Schema,
    TUserState,
    TRuleset extends Ruleset,
    TSharedStates extends SharedStates,
>(
    schema: T,
    reducer: (
        state: WithMeta<TUserState, TRuleset, TSharedStates>,
        args: SB.ExtractType<T>,
        messageInfo: MessageInfo,
    ) => void,
): UserMessageSignature<TUserState, T, TRuleset, TSharedStates> {
    return {
        schema,
        reducer,
        isAdmin: false,
    };
}

export function createAdminMessage<
    T extends SB.Schema,
    TUserState,
    TRuleset extends Ruleset,
    TSharedStates extends SharedStates,
>(
    schema: T,
    reducer: (
        state: WithMeta<TUserState, TRuleset, TSharedStates>,
        args: SB.ExtractType<T>,
        messageInfo: MessageInfo,
    ) => void,
    description = '',
): AdminMessageSignature<TUserState, T, TRuleset, TSharedStates> {
    return {
        schema,
        reducer,
        description,
        isAdmin: true,
    };
}

export type MessageItem<TName = string, TArgs = any> = {
    id: string;
    name: TName;
    timestamp: number;
    sender: string;
    args: TArgs;
};

export type Message<TMessages extends Messages = any> = {
    [K in keyof TMessages]: MessageItem<K, SB.ExtractType<TMessages[K]['schema']>>;
}[keyof TMessages & string]; // Type keyof { [key: string]: any } resolves to string | number | symbol, interestingly enough. So we force it to be a string.

export type FilterAdminMessages<TMessages extends Messages = any> = Filter<TMessages, { isAdmin: true }>;

export function createMessages<
    TState,
    TRuleset extends Ruleset = EmptyRuleset,
    TSharedStates extends SharedStates = {},
    TModules extends ReplicantModule[] = [],
>(state: SB.Schema<TState>, dependencies?: { modules?: TModules; ruleset?: TRuleset; sharedStates?: TSharedStates }) {
    return <TMessages extends Messages<TState, TRuleset, TSharedStates, TModules>>(messages: TMessages) => messages;
}
